import { apiGet, apiPost } from './client';
import type { OperationResult, RagConfig } from './types';

// /api/RagConfig — merchant-scoped RAG infrastructure config (search endpoint,
// embedding model, generation model). Read-only for the KB UI; the Settings
// tab shows it so users know exactly which Azure AI Search index backs the KB.

export async function getRagConfig(): Promise<RagConfig> {
  const wrapped = await apiGet<OperationResult<RagConfig>>('/api/RagConfig/Search');
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to load RAG config');
  }
  return wrapped.data;
}

// /api/Knowledge/TestQuery (to be added server-side) — runs a hybrid search
// against the merchant's Azure AI Search index, scoped to a specific KB, and
// returns chunk hits with score, file name, page, and excerpt. Surfaces the
// "what would the agent see for this question?" insight.
//
// While the backend endpoint is being added, this client throws a helpful
// error so the UI can show a "Backend update pending" notice.

export interface KnowledgeSearchHit {
  fileName?: string;
  citationDisplayName?: string;
  pageNumber?: number;
  score: number;
  excerpt: string;
  documentId?: string;
}

export interface KnowledgeTestQueryRequest {
  knowledgeId: number;
  query: string;
  k?: number;            // top-k results, default 5
  minimumScore?: number; // default 0.01 (RRF range)
}

export async function testKnowledgeQuery(
  input: KnowledgeTestQueryRequest
): Promise<KnowledgeSearchHit[]> {
  const wrapped = await apiPost<OperationResult<KnowledgeSearchHit[]>>(
    '/api/Knowledge/TestQuery',
    input
  );
  if (!wrapped.isSuccess) {
    throw new Error(wrapped.message || 'Test query failed');
  }
  return wrapped.data ?? [];
}
